import { PERMISSIONS } from "@shared/constants/permissions";
import NoTicketsIcon from "@shared/svg/no-tickets.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import FormSheet from "@shared/ui/FormSheet";
import { SingleSelectDropdown } from "@shared/ui/Dropdowns";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import {
  Input,
  RadioButtonInput,
  SearchInput,
  Textarea,
} from "@shared/ui/Inputs";
import { DateInput } from "@shared/ui/Inputs";
import NotFoundComponent from "@shared/ui/NotFound";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import { Info, Plus, Warning } from "phosphor-react";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import NoPMScheduled from "./_empty";
import PreventiveEvent from "./_event";
import EstimatedDurationField from "./EstimatedDurationField";
import PartsInstancePME from "./assets/PMEParts";
import ProcedureInstancePME from "./assets/PMEProcedures";
import {
  createPreventiveMaintenanceEvent,
  updatePreventiveMaintenanceEvent,
} from "../../services/preventiveMaintenance";
import { fetchListOwnOemOpenTicketsTotalCount } from "~/api";
import {
  OPEN_WORK_ORDER_LIMIT,
  shouldBlockPmeCreateForOpenLimit,
} from "~/constants/openWorkOrders";
import useAuth from "../general/_use-auth";

import {
  ADD_PREVENTIVE_MAITENANCE_EVENT,
  INITIATE_ADD_PREVENTIVE_MAITENANCE_EVENT,
  ITEMS_PER_PAGE,
} from "#/src/constants";
import { MachineHistoryLoading } from "#/src/containers/machine/loader/MachineHistoryLoading";
import { registerMixpanelEvent } from "#/src/utils/_mixpanel";
import { countImmediateWorkOrdersFromForm } from "#/src/utils/preventiveMaintenance/immediateWorkOrdersCount";
import getEnum from "$/settings/enums";
import PAID_FEATURES from "$/settings/paid-features";
import EmptySpace from "~/components/_emptySpace";
import usePermissions from "~/hooks/usePermissions";
import { useListAllOwnOemAssets } from "~/services/asset";
import { getMachineThumbnail } from "~/utils";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";

/** Date-only due: local midnight. Avoids react-calendar clamping today to `minDate=new Date()` (keeps create clock time). */
const startOfLocalDay = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return date;
  date.setHours(0, 0, 0, 0);
  return date;
};

const toEventDateIso = (value) => {
  if (value == null || value === "") return value;
  return startOfLocalDay(value).toISOString();
};

const PreventiveMaintenance = ({
  events,
  isLoadingEvents,
  machineId = null,
  showSearch = true,
  showCreateCTA = true,
  tileVariant = false,
  refetchEvents = () => {},
  isSharedAssetsPage = false,
}) => {
  const PaidFeatures = getEnum(PAID_FEATURES, "reference");
  const {
    messages: { preventiveMaintenance: PM, common, tickets },
  } = useIntl();

  const [showOpenLimitModal, setShowOpenLimitModal] = useState(false);
  const [openLimitErrorArgs, setOpenLimitErrorArgs] = useState(null);

  const { user } = useAuth();

  const allowHourlyPMEOption = user?.oem?.allowHourlyPMEOption;

  const frequency = [
    ...(allowHourlyPMEOption
      ? [{ label: PM?.frequency.hour, value: "Hour" }]
      : []),
    { label: PM?.frequency.day, value: "Day" },
    { label: PM?.frequency.week, value: "Week" },
    { label: PM?.frequency.month, value: "Month" },
    { label: PM?.frequency.year, value: "Year" },
  ];

  const estimatedDurationUnits = [
    { label: PM?.frequency.hour, value: "Hour" },
    { label: PM?.frequency.day, value: "Day" },
    { label: PM?.frequency.week, value: "Week" },
    { label: PM?.frequency.month, value: "Month" },
    { label: PM?.frequency.year, value: "Year" },
  ];

  const repetitionEnd = [
    { label: PM?.repetitionEnd.never, value: "never" },
    { label: PM?.repetitionEnd.onDate, value: "onDate" },
    { label: PM?.repetitionEnd.afterOccurrences, value: "afterOccurrences" },
  ];

  const initialEventData = {
    title: "",
    eventDate: null,
    frequency: "noRepeat",
    repeatInNumber: "",
    repetitionEnd: "never",
    repetitionEndValueDate: "",
    repetitionEndValueOccurrences: "",
    repeatIn: "",
    ticketCreationNumber: "1",
    ticketCreationIn: frequency[0].value,
    estimatedDurationNumber: "1",
    estimatedDurationIn: "Day",
    description: "",
    machineId: null,
    selectedAsset: null,
  };
  const initialEventErrorData = {
    title: "",
    ticketCreationNumber: "",
    estimatedDurationNumber: "",
    repeatInNumber: "",
    repetitionEndValueOccurrences: "",
  };
  const [eventList, setEventList] = useState([]);
  const [hasLoadedEvents, setHasLoadedEvents] = useState(false);
  const [expandedEventIds, setExpandedEventIds] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const appConfig = useGetAppConfigValue();
  const [showEditDrawer, setShowEditDrawer] = useState(null);
  const [event, setEvent] = useState(initialEventData);
  const [eventError, setEventError] = useState(initialEventErrorData);
  const [assetSearchQuery, setAssetSearchQuery] = useState("");

  const { can } = usePermissions();
  const canCreateEvents = can(PERMISSIONS.assets.create);
  const { createEvent } = createPreventiveMaintenanceEvent();
  const { updateEvent } = updatePreventiveMaintenanceEvent();

  const needsAssetSelection = !machineId;
  // Create picks the asset into `machineId`; edit carries it as `machine`.
  const selectedMachineId = machineId || event.machineId || event.machine;

  const {
    assets = [],
    handleFetchMore: handleAssetsFetchMore,
    assetsListLoading,
    totalCount: assetsTotalCount,
  } = useListAllOwnOemAssets({
    limit: ITEMS_PER_PAGE,
    searchQuery: assetSearchQuery,
    skip: 0,
    skipCondition: !needsAssetSelection || !drawerOpen,
  });

  const assetOptions = useMemo(
    () =>
      (assets || []).map((asset) => ({
        ...asset,
        label: asset.name,
        value: asset._id,
        description: asset?.serialNumber,
        thumbnail: getMachineThumbnail({
          oem: asset.oem,
          image: asset.image,
        }),
      })),
    [assets],
  );

  const handleAssetsScrollBottom = (scrollEvent) => {
    if (
      assets.length >= ITEMS_PER_PAGE &&
      !assetsListLoading &&
      scrollEvent?.previousPosition !== "above" &&
      assetsTotalCount > assets.length
    ) {
      handleAssetsFetchMore({
        limit: ITEMS_PER_PAGE,
        skip: assets.length,
      });
    }
  };

  const isFeatureAccessible = isPaidFeatureAvailable(
    PaidFeatures.preventiveMaintenance,
    user,
    appConfig,
  );

  const immediateWorkOrderCount = useMemo(
    () => countImmediateWorkOrdersFromForm(event),
    [event],
  );

  const pastCreationBannerText = useMemo(() => {
    if (immediateWorkOrderCount <= 0) return "";
    const w = PM?.warnings;
    if (immediateWorkOrderCount === 1) {
      return w?.pastCreationInlineBannerSingular ?? "";
    }
    return (w?.pastCreationInlineBannerPlural ?? "").replace(
      "{count}",
      String(immediateWorkOrderCount),
    );
  }, [immediateWorkOrderCount, PM?.warnings]);

  const openLimitModalBody = useMemo(() => {
    if (!openLimitErrorArgs) return "";
    return (PM?.warnings?.openLimitModalBody ?? "")
      .replace(
        "{immediateWorkOrderCount}",
        String(openLimitErrorArgs.immediateWorkOrderCount ?? 0),
      )
      .replace(
        "{openWorkOrdersCount}",
        String(openLimitErrorArgs.openWorkOrdersCount ?? 0),
      )
      .replace(
        "{openWorkOrderLimit}",
        String(openLimitErrorArgs.openWorkOrderLimit ?? 2000),
      );
  }, [openLimitErrorArgs, PM?.warnings?.openLimitModalBody]);

  const getOpenLimitErrorArgs = (exception) => {
    const graphQLErrors = [
      ...(exception?.graphQLErrors || []),
      ...(exception?.networkError?.result?.errors || []),
    ];
    for (const err of graphQLErrors) {
      const args = err?.extensions?.additionalArgs;
      if (args?.code === "PME_NEAR_OPEN_WO_LIMIT") return args;
    }
    return null;
  };

  const getErrorMessage = (exception) => {
    const graphQLErrors = [
      ...(exception?.graphQLErrors || []),
      ...(exception?.networkError?.result?.errors || []),
    ];
    return (
      graphQLErrors?.[0]?.message ||
      exception?.message ||
      common?.errors?.somethingWrong
    );
  };

  const shouldBlockOnOpenWorkOrderLimit = async (immediateCount) => {
    if (!immediateCount || immediateCount <= 0) return false;
    let openWorkOrdersCount = 0;
    try {
      const closedStatusId =
        user?.oem?.statuses?.[user?.oem?.statuses?.length - 1]?._id;
      openWorkOrdersCount = await fetchListOwnOemOpenTicketsTotalCount({
        closedStatusId,
      });
    } catch {
      openWorkOrdersCount = 0;
    }

    if (
      !shouldBlockPmeCreateForOpenLimit(openWorkOrdersCount, immediateCount)
    ) {
      return false;
    }

    setOpenLimitErrorArgs({
      code: "PME_NEAR_OPEN_WO_LIMIT",
      openWorkOrdersCount,
      openWorkOrderLimit: OPEN_WORK_ORDER_LIMIT,
      immediateWorkOrderCount: immediateCount,
    });
    setShowOpenLimitModal(true);
    return true;
  };

  const renderPastCreationBanner = () => {
    if (immediateWorkOrderCount <= 0 || !pastCreationBannerText) return null;
    return (
      <div
        className="w-full bg-brand-lightest border border-solid border-primary rounded-lg p-sm flex gap-sm mb-md"
        role="status"
      >
        <Warning size={18} className="text-warning shrink-0 mt-0.5" />
        <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="no-styles">
          {pastCreationBannerText}
        </BodyText>
      </div>
    );
  };

  const prepareFrequencyOptions = () => {
    return [
      { name: PM?.form.frequencyNoRepeat, value: "noRepeat" },
      { name: PM?.form.frequencyRepeat, value: "repeat" },
    ];
  };

  const prepareInfo = () => {
    let info = "";
    if (event.ticketCreationIn && event.ticketCreationNumber) {
      info = PM?.form.ticketCreationInfo.replace(
        "{{}}",
        ` ${event.ticketCreationNumber} ${
          PM?.frequency[event.ticketCreationIn.toLowerCase()]
        } `,
      );
    } else {
      info = PM?.form.ticketCreationInfo.replace(
        "{{}}",
        `1 ${PM?.frequency.day}`,
      );
    }
    return info;
  };

  const handleNoPMAddEventClick = () => {
    !isSharedAssetsPage && canCreateEvents && setDrawerOpen(true);
  };

  const inputHasError = () => {
    let err = false;

    Object.keys(eventError).map((ev) => {
      if (eventError[ev] !== "") {
        err = true;
      }
    });

    return err;
  };

  const hasValidEstimatedDuration = (ev = event) => {
    const raw = ev?.estimatedDurationNumber;
    if (raw === "" || raw === null || raw === undefined) return false;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1 && !!ev?.estimatedDurationIn;
  };

  const shouldDisableSubmit = () => {
    let disabled = true;
    if (event.title && !!event.eventDate) {
      disabled = false;
    }

    if (!selectedMachineId) {
      disabled = true;
    }

    if (!hasValidEstimatedDuration(event)) {
      disabled = true;
    }

    if (
      event.frequency === "repeat" &&
      (!event.repeatIn || !+event.repeatInNumber)
    ) {
      disabled = true;
    }

    if (event.repetitionEnd === "onDate" && event.frequency === "repeat") {
      const selectedDate = new Date(event.repetitionEndValueDate);
      const dueDate = new Date(event.eventDate);
      dueDate.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      if (!event.repetitionEndValueDate || selectedDate <= dueDate) {
        disabled = true;
      }
    }

    if (
      event.repetitionEnd === "afterOccurrences" &&
      event.frequency === "repeat"
    ) {
      if (
        !event.repetitionEndValueOccurrences ||
        event.repetitionEndValueOccurrences <= 0 ||
        isNaN(parseInt(event.repetitionEndValueOccurrences))
      ) {
        disabled = true;
      }
    }

    return disabled;
  };

  const handleEstimatedDurationNumberChange = (e) => {
    const v = e.target.value;
    const n = Number(v);

    if (!v || n < 1 || !Number.isFinite(n)) {
      setEventError({
        ...eventError,
        estimatedDurationNumber: PM?.error.zeroOrEmptyNotAllowed,
      });
    } else {
      setEventError({
        ...eventError,
        estimatedDurationNumber: "",
      });
    }
    setEvent({
      ...event,
      estimatedDurationNumber: e.target.value,
    });
  };

  const enforceEstimatedDurationOrBlock = () => {
    if (hasValidEstimatedDuration(event)) return true;
    setEventError({
      ...eventError,
      estimatedDurationNumber: PM?.error.zeroOrEmptyNotAllowed,
    });
    return false;
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setShowOpenLimitModal(false);
    setOpenLimitErrorArgs(null);
    setEventError(initialEventErrorData);
    setEvent(initialEventData);
    setAssetSearchQuery("");
  };

  const handleEditDrawerClose = () => {
    setDrawerOpen(false);
    setShowOpenLimitModal(false);
    setOpenLimitErrorArgs(null);
    setEventError(initialEventErrorData);
    setEvent(initialEventData);
    setShowEditDrawer(null);
  };

  const buildCreatePayload = () => {
    const repetitive = event.frequency === "repeat";
    const repeatEventInDefault = repetitive ? frequency[2].value : undefined;
    const repeatInNumberDefault = repetitive ? 6 : 0;

    return {
      ...event,
      ...(event.repetitionEnd === "onDate"
        ? {
            repetitionEndValueDate: new Date(
              event.repetitionEndValueDate,
            )?.toISOString(),
            repetitionEndValueOccurrences: 1,
          }
        : event.repetitionEnd === "afterOccurrences"
        ? {
            repetitionEndValueOccurrences: parseInt(
              event.repetitionEndValueOccurrences || "1",
            ),
            repetitionEndValueDate: null,
          }
        : {
            repetitionEndValueOccurrences: 1,
            repetitionEndValueDate: null,
          }),
      eventDate: toEventDateIso(event.eventDate),
      startDate: new Date().toISOString(),
      timezoneOffset: new Date().getTimezoneOffset(),
      title: event.title.trim(),
      description: (event.description ?? "").trim(),
      repeatIn: event.repeatIn || repeatEventInDefault,
      repeatInNumber: parseInt(event.repeatInNumber) || repeatInNumberDefault,
      ticketCreationIn: event.ticketCreationIn || frequency[0].value,
      ticketCreationNumber: parseInt(event.ticketCreationNumber) || 1,
      estimatedDurationIn: event.estimatedDurationIn,
      estimatedDurationNumber: parseInt(event.estimatedDurationNumber, 10),
      oem: user?.oem?._id,
      machine: selectedMachineId,
      procedures:
        event?.procedures?.map((proc) => ({ procedure: proc.procedure._id })) ||
        [],
      inventoryParts: event?.inventoryParts
        ?.filter((ev) => ev.quantity > 0)
        ?.map((ev) => ({
          part: ev.part._id,
          quantity: ev.quantity,
        })),
    };
  };

  const executeCreateEvent = async () => {
    const payload = buildCreatePayload();
    // Dropdown / form-only fields must not be sent to GraphQL
    delete payload.selectedAsset;
    delete payload.machineId;
    if (!payload.machine) {
      toast(
        <Toast type={TOAST_TYPES.ERROR} message={PM?.form?.assetRequired} />,
        {
          closeButton: false,
        },
      );
      return;
    }
    if (payload.repetitionEnd === "never" || payload.frequency === "noRepeat") {
      delete payload.repetitionEndValueDate;
      delete payload.repetitionEndValueOccurrences;
      delete payload.repetitionEndValue;
    }
    const shouldBlock = await shouldBlockOnOpenWorkOrderLimit(
      immediateWorkOrderCount,
    );
    if (shouldBlock) return;

    registerMixpanelEvent(ADD_PREVENTIVE_MAITENANCE_EVENT);
    try {
      await createEvent(payload);
      setDrawerOpen(false);
      setEventError(initialEventErrorData);
      setEvent(initialEventData);
      toast(
        <Toast type={TOAST_TYPES.SUCCESS} message={PM?.successCreation} />,
        {
          closeButton: false,
        },
      );
    } catch (exception) {
      const openLimitArgs = getOpenLimitErrorArgs(exception);
      if (openLimitArgs) {
        setOpenLimitErrorArgs(openLimitArgs);
        setShowOpenLimitModal(true);
        return;
      }
      toast(
        <Toast type={TOAST_TYPES.ERROR} message={getErrorMessage(exception)} />,
        {
          closeButton: false,
        },
      );
    }
  };

  const handleSubmit = async () => {
    if (inputHasError() || !enforceEstimatedDurationOrBlock()) return;
    await executeCreateEvent();
  };

  const buildUpdatePayload = () => {
    const repetitive = event.frequency === "repeat";
    const repeatEventInDefault = repetitive ? frequency[2].value : undefined;
    const repeatInNumberDefault = repetitive ? 6 : 0;

    return {
      ...event,
      ...(event.repetitionEnd === "onDate"
        ? {
            repetitionEndValueDate: new Date(
              event.repetitionEndValueDate,
            )?.toISOString(),
            repetitionEndValueOccurrences: 1,
          }
        : event.repetitionEnd === "afterOccurrences"
        ? {
            repetitionEndValueOccurrences: parseInt(
              event.repetitionEndValueOccurrences || "1",
            ),
            repetitionEndValueDate: null,
          }
        : {
            repetitionEndValueOccurrences: 1,
            repetitionEndValueDate: null,
          }),
      eventDate: toEventDateIso(event.eventDate),
      title: event.title.trim(),
      description: (event.description ?? "").trim(),
      repeatIn: event.repeatIn || repeatEventInDefault,
      repeatInNumber: parseInt(event.repeatInNumber) || repeatInNumberDefault,
      ticketCreationIn: event.ticketCreationIn || frequency[0].value,
      ticketCreationNumber: parseInt(event.ticketCreationNumber) || 1,
      estimatedDurationIn: event.estimatedDurationIn,
      estimatedDurationNumber: parseInt(event.estimatedDurationNumber, 10),
      oem: user?.oem?._id,
      machine: machineId || event.machine,
      procedures:
        event?.procedures?.map((proc) => ({ procedure: proc.procedure._id })) ||
        [],
      inventoryParts: event?.inventoryParts?.map((ev) => ({
        part: ev.part._id,
        quantity: ev.quantity,
      })),
    };
  };

  const executeUpdateEvent = async () => {
    const payload = buildUpdatePayload();
    if (payload.repetitionEnd === "never" || payload.frequency === "noRepeat") {
      delete payload.repetitionEndValueDate;
      delete payload.repetitionEndValueOccurrences;
      delete payload.repetitionEndValue;
    }
    const shouldBlock = await shouldBlockOnOpenWorkOrderLimit(
      immediateWorkOrderCount,
    );
    if (shouldBlock) return;
    try {
      await updateEvent(payload);

      setShowEditDrawer(null);
      setEvent(initialEventData);
      setEventError(initialEventErrorData);
      toast(
        <Toast type={TOAST_TYPES.SUCCESS} message={PM?.successModification} />,
        {
          closeButton: false,
        },
      );
    } catch (exception) {
      const openLimitArgs = getOpenLimitErrorArgs(exception);
      if (openLimitArgs) {
        setOpenLimitErrorArgs(openLimitArgs);
        setShowOpenLimitModal(true);
        return;
      }
      toast(
        <Toast type={TOAST_TYPES.ERROR} message={getErrorMessage(exception)} />,
        {
          closeButton: false,
        },
      );
    }
  };

  const handleUpdateEvent = async () => {
    if (inputHasError() || !enforceEstimatedDurationOrBlock()) return;

    await executeUpdateEvent();
  };

  const handleSearchEvent = (value) => {
    if (value) {
      const filteredEvents = events?.filter((ev) =>
        ev.title?.toLowerCase()?.includes(value?.toLowerCase()),
      );
      return setEventList(filteredEvents);
    } else {
      return setEventList(events);
    }
  };

  useEffect(() => {
    if (events?.length) {
      setEventList(events);
    } else if (!isLoadingEvents) {
      setEventList([]);
    }
  }, [events, isLoadingEvents]);

  useEffect(() => {
    if (!isLoadingEvents) {
      setHasLoadedEvents(true);
    }
  }, [isLoadingEvents]);

  if (isLoadingEvents && !hasLoadedEvents) {
    return <MachineHistoryLoading />;
  }

  return (
    <>
      <div className="h-full -mt-md">
        <div className="preventive-maintenance-header">
          <div className="tab-inner-content-header user-accounts-header">
            {canCreateEvents && !isSharedAssetsPage && showCreateCTA && (
              <button
                onClick={() => {
                  registerMixpanelEvent(
                    INITIATE_ADD_PREVENTIVE_MAITENANCE_EVENT,
                  );
                  if (isFeatureAccessible) {
                    setDrawerOpen(true);
                  } else {
                    setShowAlert(true);
                  }
                }}
                className="btn-v2 link-btn-v2 btn-icon-left"
              >
                <Plus size={14} /> {PM?.createEventButtonText}
              </button>
            )}

            {showSearch && (
              <SearchInput
                placeholder={common?.searchText}
                onChange={handleSearchEvent}
              />
            )}
          </div>
        </div>
        {!events?.length ? (
          <NoPMScheduled
            addEventCallback={handleNoPMAddEventClick}
            canCreate={canCreateEvents}
            isSharedAssetsPage={isSharedAssetsPage}
          />
        ) : (
          <>
            <div
              className={`preventive-maintenance-content mt-xs ${
                tileVariant ? "" : "flex flex-col gap-md"
              }`}
            >
              {eventList?.map((evt) => {
                return (
                  <div
                    key={evt._id}
                    className="preventive-maintenance-event-wrapper"
                  >
                    <PreventiveEvent
                      eventId={evt._id}
                      title={evt.title}
                      startDate={evt.startDate}
                      createdAt={evt.created_at}
                      repeatInNumber={evt.repeatInNumber}
                      endsOn={evt.endsOn}
                      lastCompletedAt={evt.lastCompleted}
                      allOccurrencesDone={evt.allOccurrencesDone}
                      repeatIn={evt.repeatIn}
                      eventDate={evt.eventDate}
                      description={evt.description}
                      frequency={evt.frequency}
                      nextCreationDate={evt.nextCreationDate}
                      ticketCreationNumber={evt.ticketCreationNumber}
                      ticketCreationIn={evt.ticketCreationIn}
                      estimatedDurationNumber={evt.estimatedDurationNumber}
                      estimatedDurationIn={evt.estimatedDurationIn}
                      occurrenceSchedule={evt.occurrenceSchedule}
                      repetitionEnd={evt.repetitionEnd}
                      repetitionEndValueDate={evt.repetitionEndValueDate}
                      repetitionEndValueOccurrences={
                        evt.repetitionEndValueOccurrences
                      }
                      machineId={machineId || evt.machine._id}
                      machineName={evt.machine.name}
                      machineFacilityName={
                        evt.machine.customer?.name || tickets?.deletedConnection
                      }
                      machineFacilityId={evt.machine.customer?._id}
                      refetchEvents={refetchEvents}
                      isExpanded={!!expandedEventIds[evt._id]}
                      onToggleExpand={() =>
                        setExpandedEventIds((prev) => ({
                          ...prev,
                          [evt._id]: !prev[evt._id],
                        }))
                      }
                      onEditButtonClick={(data) => {
                        setEvent(data);
                        setShowEditDrawer(evt._id);
                      }}
                      createdBy={evt.createdBy}
                      tileVariant={tileVariant}
                      procedures={evt?.procedures}
                      inventoryParts={evt?.inventoryParts}
                      isSharedAssetsPage={isSharedAssetsPage}
                    />
                    {/* Drawer for update event */}
                    <FormSheet
                      title={PM?.editEventTitleText}
                      open={showEditDrawer === evt._id}
                      onClose={handleEditDrawerClose}
                      onCancel={handleEditDrawerClose}
                      onSubmit={handleUpdateEvent}
                      submitDisabled={shouldDisableSubmit() || inputHasError()}
                      submitText={common.update}
                      cancelDisabled={false}
                      cancelText={common.cancel}
                    >
                      <div className="preventive-maintenance-drawer-content-wrapper update-event-modal-content">
                        <Input
                          label={`${PM?.form.eventName} *`}
                          keyId="pm-event-name"
                          placeholder={PM?.form.eventNamePlaceholder}
                          value={event.title}
                          errorMessage={eventError.title}
                          onChange={(e) => {
                            const eventTitleLength = e.target.value.length;

                            if (eventTitleLength > 75) {
                              return setEventError({
                                title: PM?.eventNameCharacterLimitError,
                              });
                            } else {
                              return setEvent({
                                ...event,
                                title: e.target.value,
                              });
                            }
                          }}
                        />

                        <EmptySpace height="16px" />

                        <DateInput
                          keyId="edit-event-date"
                          onChange={(e) => {
                            setEvent({
                              ...event,
                              eventDate: toEventDateIso(e),
                            });
                          }}
                          placeholder={PM?.form.startDatePlaceholder}
                          value={event.eventDate || ""}
                          label={`${PM?.form.eventDate} *`}
                          dropPosition="auto"
                          format={user?.dateFormat}
                          minDate={startOfLocalDay(new Date())}
                        />
                        <EmptySpace height="16px" />

                        <EstimatedDurationField
                          keyPrefix={`update-${evt._id}`}
                          valueNumber={event.estimatedDurationNumber}
                          valueUnit={event.estimatedDurationIn}
                          unitOptions={estimatedDurationUnits}
                          errorMessage={eventError.estimatedDurationNumber}
                          onNumberChange={handleEstimatedDurationNumberChange}
                          onUnitChange={(selected) =>
                            setEvent({
                              ...event,
                              estimatedDurationIn: selected[0]?.value || "Day",
                            })
                          }
                        />
                        {renderPastCreationBanner()}

                        <EmptySpace height="16px" />

                        <RadioButtonInput
                          keyId={`update-frequency-select-${evt._id}`}
                          label={PM?.form.frequencyLabel}
                          options={prepareFrequencyOptions()}
                          value={event.frequency}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "repeat") {
                              setEvent({
                                ...event,
                                frequency: v,
                                repeatInNumber: evt.repeatInNumber || 6,
                                repeatIn: evt.repeatIn || frequency[2].value,
                              });
                            } else {
                              setEvent({
                                ...event,
                                frequency: v,
                                repeatInNumber: "",
                                repeatIn: "",
                              });
                              setEventError({
                                ...eventError,
                                repeatInNumber: "",
                              });
                            }
                          }}
                        />

                        <EmptySpace height="16px" />

                        <BodyText
                          size={BODY_TEXT_SIZES.X_SMALL}
                          color={
                            event.frequency !== "repeat"
                              ? "text-disabled"
                              : "text-secondary"
                          }
                          className="mb-sm"
                        >
                          {PM?.form.repeatEventEvery}{" "}
                          {event.repeatIn
                            ? `${event.repeatInNumber} ${
                                PM?.frequency[event.repeatIn.toLowerCase()]
                              }`
                            : ""}
                        </BodyText>

                        <div className="input-group">
                          {/* repeatEventEvery */}
                          <div className="max-w-[35%] mr-lg mb-sm">
                            <Input
                              keyId="update-frequency-number"
                              type="number"
                              value={event.repeatInNumber}
                              placeholder="6"
                              disabled={event.frequency !== "repeat"}
                              onKeyDown={(e) => {
                                if (e.key === ".") {
                                  e.preventDefault();
                                }
                              }}
                              errorMessage={eventError.repeatInNumber}
                              onChange={(e) => {
                                const v = e.target.value;
                                const n = Number(v);

                                if (!v || n < 1) {
                                  setEventError({
                                    ...eventError,
                                    repeatInNumber:
                                      PM?.error.zeroOrEmptyNotAllowed,
                                  });
                                } else {
                                  setEventError({
                                    ...eventError,
                                    repeatInNumber: "",
                                  });
                                }
                                setEvent({
                                  ...event,
                                  repeatInNumber: e.target.value,
                                });
                              }}
                            />
                          </div>

                          <SingleSelectDropdown
                            keyId="edit-repeat-frequency"
                            options={frequency.filter(
                              (f) => f.value !== "Hour",
                            )}
                            disabled={event.frequency !== "repeat"}
                            searchable
                            placeholder={frequency[2]?.label}
                            onChange={(selected) =>
                              setEvent({
                                ...event,
                                repeatIn: selected[0]?.value,
                              })
                            }
                            values={
                              event.repeatIn
                                ? [
                                    {
                                      label:
                                        PM?.frequency[
                                          event.repeatIn.toLowerCase()
                                        ],
                                      value: event.repeatIn,
                                    },
                                  ]
                                : []
                            }
                          />
                        </div>

                        {event.frequency === "repeat" && (
                          <div className="input-group gap-x-lg">
                            <div className="max-w-[35%] inline-block">
                              <BodyText
                                size={BODY_TEXT_SIZES.X_SMALL}
                                className="mb-sm"
                                color="text-secondary"
                              >
                                {PM?.form.repetitionEnd}
                              </BodyText>
                              <SingleSelectDropdown
                                keyId="update-repeat-end-frequency"
                                className="[&_.react-dropdown-select-input]:!h-0"
                                options={repetitionEnd}
                                searchable
                                placeholder={repetitionEnd[0]?.label}
                                onChange={(selected) =>
                                  setEvent({
                                    ...event,
                                    repetitionEnd: selected[0]?.value,
                                    ...(selected[0]?.value === "never"
                                      ? {
                                          repetitionEndValueDate: "",
                                          repetitionEndValueOccurrences: "",
                                        }
                                      : selected[0]?.value === "onDate" &&
                                        event.repetitionEnd !== "onDate"
                                      ? { repetitionEndValueOccurrences: "" }
                                      : selected[0]?.value ===
                                          "afterOccurrences" &&
                                        event.repetitionEnd !==
                                          "afterOccurrences"
                                      ? {
                                          repetitionEndValueOccurrences: "",
                                        }
                                      : {}),
                                  })
                                }
                                values={
                                  event.repetitionEnd
                                    ? [
                                        {
                                          label:
                                            PM?.repetitionEnd[
                                              event.repetitionEnd
                                            ],
                                          value: event.repetitionEnd,
                                        },
                                      ]
                                    : []
                                }
                              />
                            </div>
                            {event.repetitionEnd === "afterOccurrences" ? (
                              <Input
                                keyId="update-repetition-end-occurrences"
                                type="number"
                                className="mt-2xl"
                                value={event.repetitionEndValueOccurrences}
                                placeholder="5"
                                onKeyDown={(e) => {
                                  if (e.key === ".") e.preventDefault();
                                }}
                                errorMessage={
                                  eventError.repetitionEndValueOccurrences
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const n = Number(v);
                                  if (!v || n < 1) {
                                    setEventError({
                                      ...eventError,
                                      repetitionEndValueOccurrences:
                                        PM?.error.zeroOrEmptyNotAllowed,
                                    });
                                  } else {
                                    setEventError({
                                      ...eventError,
                                      repetitionEndValueOccurrences: "",
                                    });
                                  }
                                  setEvent({
                                    ...event,
                                    repetitionEndValueOccurrences:
                                      e.target.value,
                                  });
                                }}
                              />
                            ) : event.repetitionEnd === "onDate" ? (
                              <DateInput
                                keyId="update-event-end-date"
                                onChange={(e) => {
                                  setEvent({
                                    ...event,
                                    repetitionEndValueDate: e,
                                  });
                                }}
                                placeholder="DD/MM/YYYY"
                                format={user?.dateFormat}
                                value={event.repetitionEndValueDate}
                                label={PM?.form.endDate}
                                errorMessage={(() => {
                                  if (!event.repetitionEndValueDate) return "";
                                  const selectedDate = new Date(
                                    event.repetitionEndValueDate,
                                  );
                                  const dueDate = new Date(event.eventDate);
                                  dueDate.setHours(0, 0, 0, 0);
                                  selectedDate.setHours(0, 0, 0, 0);
                                  return selectedDate <= dueDate
                                    ? PM?.error?.endDateOnOrBeforeDueDate
                                    : "";
                                })()}
                                dropPosition="auto"
                                dropAlignment="right"
                                minDate={
                                  new Date(
                                    new Date(event.eventDate).setDate(
                                      new Date(event.eventDate).getDate() + 1,
                                    ),
                                  )
                                }
                              />
                            ) : null}
                          </div>
                        )}

                        <BodyText
                          size={BODY_TEXT_SIZES.X_SMALL}
                          color="text-secondary"
                          className="mb-sm"
                        >
                          {prepareInfo()}
                        </BodyText>

                        <div className="input-group">
                          <div className="max-w-[35%] mr-lg mb-sm">
                            <Input
                              keyId="update-ticket-frequency-number"
                              type="number"
                              value={event.ticketCreationNumber}
                              placeholder="1"
                              onKeyDown={(e) => {
                                if (e.key === ".") {
                                  e.preventDefault();
                                }
                              }}
                              errorMessage={eventError.ticketCreationNumber}
                              onChange={(e) => {
                                const v = e.target.value;
                                const n = Number(v);

                                if (!v || n < 1) {
                                  setEventError({
                                    ...eventError,
                                    ticketCreationNumber:
                                      PM?.error.zeroOrEmptyNotAllowed,
                                  });
                                } else {
                                  setEventError({
                                    ...eventError,
                                    ticketCreationNumber: "",
                                  });
                                }
                                setEvent({
                                  ...event,
                                  ticketCreationNumber: e.target.value,
                                });
                              }}
                            />
                          </div>

                          <SingleSelectDropdown
                            keyId="edit-time-before"
                            options={frequency}
                            searchable
                            placeholder={frequency[0]?.label}
                            onChange={(selected) =>
                              setEvent({
                                ...event,
                                ticketCreationIn: selected[0]?.value,
                              })
                            }
                            values={
                              event.ticketCreationIn
                                ? [
                                    {
                                      label:
                                        PM?.frequency[
                                          event.ticketCreationIn.toLowerCase()
                                        ],
                                      value: event.ticketCreationIn,
                                    },
                                  ]
                                : [
                                    {
                                      label: frequency[0].label,
                                      value: frequency[0].value,
                                    },
                                  ]
                            }
                          />
                        </div>

                        <EmptySpace height="16px" />

                        <Textarea
                          label={PM?.form.description}
                          keyId="add-event-description"
                          placeholder={PM?.form.descriptionPlaceholder}
                          value={event.description}
                          onChange={(e) =>
                            setEvent({
                              ...event,
                              description: e.target.value,
                            })
                          }
                        />
                        <ProcedureInstancePME
                          isClosedTicket={false}
                          handleDrawerClose={handleDrawerClose}
                          preventiveMaintenanceId={null}
                          setEventCb={setEvent}
                          event={event}
                        />
                        <PartsInstancePME
                          isClosedTicket={false}
                          handleDrawerClose={handleDrawerClose}
                          preventiveMaintenanceId={null}
                          setEventCb={setEvent}
                          event={event}
                          machineId={machineId || evt?.machine?._id}
                        />
                      </div>
                    </FormSheet>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <AlertBox
        acceptButtonText={PM?.noPMAlertOkayButton}
        image={<NoTicketsIcon width="130" height="130" />}
        alertContent={
          <Headline size={HEADLINE_SIZES.X_SMALL}>
            {PM?.noPMAlertTitle}{" "}
            <a
              href={`mailto:${PM?.noPMAlertTitleEmail}`}
              className="text-brand"
            >
              {PM?.noPMAlertTitleEmail}
            </a>
          </Headline>
        }
        onAccept={() => {
          setShowAlert(false);
        }}
        overlay
        isOpen={showAlert}
      />

      <FormSheet
        title={PM?.drawerTitle}
        open={drawerOpen}
        onClose={handleDrawerClose}
        onSubmit={handleSubmit}
        submitDisabled={shouldDisableSubmit() || inputHasError()}
        submitText={PM?.createButton}
      >
        <div className="preventive-maintenance-drawer-content-wrapper">
          <div className="w-full bg-brand-lightest p-sm flex rounded-lg gap-sm mt-2xs mb-2xl">
            <Info size={16} className="text-secondary" />
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="no-styles">
              {PM?.addEventBannerMessage}
            </BodyText>
          </div>
          {needsAssetSelection && (
            <>
              <SingleSelectDropdown
                keyId="pm-event-asset"
                label={`${PM?.assetTitle} *`}
                placeholder={PM?.form?.assetPlaceholder}
                options={assetOptions}
                onChange={(selected) => {
                  const asset = selected?.[0] || null;
                  const nextMachineId = asset?._id || asset?.value || null;
                  const assetChanged = nextMachineId !== event.machineId;
                  setEvent({
                    ...event,
                    machineId: nextMachineId,
                    selectedAsset: asset,
                    ...(assetChanged ? { inventoryParts: [] } : {}),
                  });
                }}
                searchable
                searchBy="label, serialNumber"
                isAsync
                asyncProps={{
                  isLoading: assetsListLoading,
                  onScrollToBottom: handleAssetsScrollBottom,
                  onSearch: (value) => {
                    if (assetSearchQuery !== value) {
                      setAssetSearchQuery(value);
                    }
                  },
                }}
                values={event.selectedAsset ? [event.selectedAsset] : []}
              />
              <EmptySpace height="16px" />
            </>
          )}
          <Input
            label={`${PM?.form.eventName} *`}
            keyId="pm-event-name"
            placeholder={PM?.form.eventNamePlaceholder}
            value={event.title}
            errorMessage={eventError.title}
            onChange={(e) => {
              const eventTitleLength = e.target.value.length;

              if (eventTitleLength > 75) {
                return setEventError({
                  title: PM?.eventNameCharacterLimitError,
                });
              } else {
                setEvent({ ...event, title: e.target.value });
                return setEventError({
                  ...eventError,
                  title: "",
                });
              }
            }}
          />
          <EmptySpace height="16px" />
          <DateInput
            keyId="create-event-date"
            onChange={(e) => {
              setEvent({
                ...event,
                eventDate: toEventDateIso(e),
              });
            }}
            placeholder={PM?.form.startDatePlaceholder}
            value={event.eventDate || ""}
            label={`${PM?.form.eventDate} *`}
            dropPosition="auto"
            minDate={startOfLocalDay(new Date())}
            format={user?.dateFormat}
          />

          <EmptySpace height="16px" />
          <EstimatedDurationField
            keyPrefix="create"
            valueNumber={event.estimatedDurationNumber}
            valueUnit={event.estimatedDurationIn}
            unitOptions={estimatedDurationUnits}
            errorMessage={eventError.estimatedDurationNumber}
            onNumberChange={handleEstimatedDurationNumberChange}
            onUnitChange={(selected) =>
              setEvent({
                ...event,
                estimatedDurationIn: selected[0]?.value || "Day",
              })
            }
          />
          {renderPastCreationBanner()}

          <EmptySpace height="16px" />

          <RadioButtonInput
            keyId="frequency-select"
            label={PM?.form.frequencyLabel}
            options={prepareFrequencyOptions()}
            value={event.frequency}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "repeat") {
                setEvent({
                  ...event,
                  frequency: v,
                  repeatInNumber: "6",
                  repeatIn: frequency[2].value,
                });
              } else {
                setEvent({
                  ...event,
                  frequency: v,
                  repeatInNumber: 0,
                  repeatIn: "",
                });

                setEventError({
                  ...eventError,
                  repeatInNumber: "",
                });
              }
            }}
          />
          <EmptySpace height="16px" />
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color={
              event.frequency !== "repeat" ? "text-disabled" : "text-secondary"
            }
            className="mb-sm"
          >
            {PM?.form.repeatEventEvery}{" "}
            {event.repeatIn && event.repeatInNumber
              ? `${event.repeatInNumber} ${
                  PM?.frequency[event.repeatIn.toLowerCase()]
                }`
              : ""}
          </BodyText>
          <div className="input-group">
            <div className="max-w-[35%] mr-lg mb-sm">
              <Input
                keyId="frequency-number"
                type="number"
                value={event.repeatInNumber}
                placeholder="6"
                disabled={event.frequency !== "repeat"}
                errorMessage={eventError.repeatInNumber}
                onKeyDown={(e) => {
                  if (e.key === ".") {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  const n = Number(v);

                  if (!v || n < 1) {
                    setEventError({
                      ...eventError,
                      repeatInNumber: PM?.error.zeroOrEmptyNotAllowed,
                    });
                  } else {
                    setEventError({
                      ...eventError,
                      repeatInNumber: "",
                    });
                  }

                  setEvent({
                    ...event,
                    repeatInNumber: e.target.value,
                  });
                }}
              />
            </div>

            <SingleSelectDropdown
              keyId="add-repeat-frequency"
              options={frequency.filter((f) => f.value !== "Hour")}
              disabled={event.frequency !== "repeat"}
              searchable
              placeholder={frequency[2]?.label}
              onChange={(selected) => {
                setEvent({
                  ...event,
                  repeatIn: selected[0]?.value,
                });
              }}
              onKeyDown={(e) => {
                if (e.key === ".") {
                  e.preventDefault();
                }
              }}
              values={
                event.repeatIn
                  ? [
                      {
                        label: PM?.frequency[event.repeatIn.toLowerCase()],
                        value: event.repeatIn,
                      },
                    ]
                  : [frequency[2]]
              }
            />
          </div>

          {event.frequency === "repeat" && (
            <>
              <div className="input-group gap-x-lg">
                <div className="max-w-[35%] inline-block">
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    className="mb-sm"
                    color="text-secondary"
                  >
                    {PM?.form.repetitionEnd}
                  </BodyText>
                  <SingleSelectDropdown
                    keyId="edit-repeat-end-frequency"
                    className="[&_.react-dropdown-select-input]:!h-0"
                    options={repetitionEnd}
                    searchable
                    placeholder={repetitionEnd[0]?.label}
                    onChange={(selected) =>
                      setEvent({
                        ...event,
                        repetitionEnd: selected[0]?.value,
                        ...(selected[0]?.value === "never"
                          ? {
                              repetitionEndValueDate: "",
                              repetitionEndValueOccurrences: "",
                            }
                          : selected[0]?.value === "onDate" &&
                            event.repetitionEnd !== "onDate"
                          ? {
                              repetitionEndValueOccurrences: "",
                            }
                          : selected[0]?.value === "afterOccurrences" &&
                            event.repetitionEnd !== "afterOccurrences"
                          ? {
                              repetitionEndValueOccurrences: "",
                            }
                          : {}),
                      })
                    }
                    values={
                      event.repetitionEnd
                        ? [
                            {
                              label: PM?.repetitionEnd[event.repetitionEnd],
                              value: event.repetitionEnd,
                            },
                          ]
                        : []
                    }
                  />
                </div>
                {event.repetitionEnd === "afterOccurrences" ? (
                  <Input
                    keyId="update-repetition-end-value"
                    type="number"
                    className="mt-2xl"
                    value={event.repetitionEndValueOccurrences}
                    placeholder="5"
                    disabled={event.repetitionEnd !== "afterOccurrences"}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                    errorMessage={eventError.repetitionEndValueOccurrences}
                    onChange={(e) => {
                      const v = e.target.value;
                      const n = Number(v);

                      if (!v || n < 1) {
                        setEventError({
                          ...eventError,
                          repetitionEndValueOccurrences:
                            PM?.error.zeroOrEmptyNotAllowed,
                        });
                      } else {
                        setEventError({
                          ...eventError,
                          repetitionEndValueOccurrences: "",
                        });
                      }

                      setEvent({
                        ...event,
                        repetitionEndValueOccurrences: e.target.value,
                      });
                    }}
                  />
                ) : event.repetitionEnd === "onDate" ? (
                  <DateInput
                    keyId="edit-event-end-date"
                    onChange={(e) => {
                      setEvent({
                        ...event,
                        repetitionEndValueDate: e,
                      });
                    }}
                    placeholder="DD/MM/YYYY"
                    value={event.repetitionEndValueDate}
                    label={PM?.form.endDate}
                    format={user?.dateFormat}
                    errorMessage={(() => {
                      if (!event.repetitionEndValueDate) return "";
                      const selectedDate = new Date(
                        event.repetitionEndValueDate,
                      );
                      const dueDate = new Date(event.eventDate);
                      dueDate.setHours(0, 0, 0, 0);
                      selectedDate.setHours(0, 0, 0, 0);
                      return selectedDate <= dueDate
                        ? PM?.error?.endDateOnOrBeforeDueDate
                        : "";
                    })()}
                    dropPosition="auto"
                    dropAlignment="right"
                    minDate={
                      new Date(
                        new Date(event.eventDate).setDate(
                          new Date(event.eventDate).getDate() + 1,
                        ),
                      )
                    }
                  />
                ) : null}
              </div>
            </>
          )}

          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color="text-secondary"
            className="my-lg"
          >
            {prepareInfo()}
          </BodyText>
          <div className="input-group">
            <div className="max-w-[35%] mr-lg mb-sm">
              <Input
                keyId="ticket-creation-frequency-number"
                type="number"
                value={event.ticketCreationNumber}
                placeholder="1"
                errorMessage={eventError.ticketCreationNumber}
                onKeyDown={(e) => {
                  if (e.key === ".") {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  const n = Number(v);

                  if (!v || n < 1) {
                    setEventError({
                      ...eventError,
                      ticketCreationNumber: PM?.error.zeroOrEmptyNotAllowed,
                    });
                  } else {
                    setEventError({
                      ...eventError,
                      ticketCreationNumber: "",
                    });
                  }

                  setEvent({
                    ...event,
                    ticketCreationNumber: e.target.value,
                  });
                }}
              />
            </div>

            <SingleSelectDropdown
              keyId="add-time-before"
              options={frequency}
              searchable
              placeholder={frequency[0]?.label}
              onChange={(selected) => {
                setEvent({
                  ...event,
                  ticketCreationIn: selected[0]?.value,
                });
              }}
              values={
                event.ticketCreationIn
                  ? [
                      {
                        label: event.ticketCreationIn,
                        value: event.ticketCreationIn,
                      },
                    ]
                  : [
                      {
                        label: frequency[0].value,
                        value: frequency[0].value,
                      },
                    ]
              }
            />
          </div>
          <EmptySpace height="16px" />
          <Textarea
            label={PM?.form.description}
            keyId="add-event-description"
            placeholder={PM?.form.descriptionPlaceholder}
            value={event.description}
            onChange={(e) =>
              setEvent({
                ...event,
                description: e.target.value,
              })
            }
          />
          <ProcedureInstancePME
            isClosedTicket={false}
            handleDrawerClose={handleDrawerClose}
            preventiveMaintenanceId={null}
            setEventCb={setEvent}
            event={event}
          />
          <PartsInstancePME
            isClosedTicket={false}
            handleDrawerClose={handleDrawerClose}
            preventiveMaintenanceId={null}
            setEventCb={setEvent}
            event={event}
            machineId={selectedMachineId}
          />
        </div>
      </FormSheet>

      <AlertBox
        isOpen={showOpenLimitModal}
        acceptButtonText={PM?.warnings?.openLimitModalButton ?? common?.okay}
        image={<NoTicketsIcon width="130" height="130" />}
        alertContent={
          <>
            <Headline size={HEADLINE_SIZES.X_SMALL}>
              {PM?.warnings?.openLimitModalTitle}
            </Headline>
            <BodyText
              size={BODY_TEXT_SIZES.X_SMALL}
              color="text-secondary"
              className="mt-sm"
            >
              {openLimitModalBody}
            </BodyText>
          </>
        }
        onAccept={() => {
          setShowOpenLimitModal(false);
          setOpenLimitErrorArgs(null);
        }}
        overlay
      />
    </>
  );
};

export default React.memo(PreventiveMaintenance);
